/**
 * The device's notification switches (Settings > Notifications), kept on
 * this device like Settings > Sounds. "Enable desktop notifications" is the
 * account's (`desktop_notifications`); "Notification sounds" is the message
 * sound's own switch in Settings > Sounds, shown in both places.
 */
import { create } from 'zustand';
import { storage } from '../lib/storage';
import { useSounds } from '../sound/sounds';

export interface NotifyPrefs {
  /** The message text in the toast; off: "New message from X". */
  preview: boolean;
  /** The count on the taskbar icon (desktop) or the tab's title and icon (web). */
  badge: boolean;
  /** Flash the taskbar (desktop) or the tab's title (web) for DMs, calls and pokes. */
  flash: boolean;
}

const KEY = 'notify-prefs';
const DEFAULTS: NotifyPrefs = { preview: true, badge: true, flash: true };

interface NotifyPrefsStore {
  prefs: NotifyPrefs;
  setPrefs(p: Partial<NotifyPrefs>): void;
}

export const useNotifyPrefs = create<NotifyPrefsStore>((set) => ({
  prefs: { ...DEFAULTS, ...storage.getJson<Partial<NotifyPrefs>>(KEY, {}) },
  setPrefs(p) {
    set((st) => {
      const prefs = { ...st.prefs, ...p };
      storage.setJson(KEY, prefs);
      return { prefs };
    });
  },
}));

export function notifyPrefs(): NotifyPrefs {
  return useNotifyPrefs.getState().prefs;
}

/** "Notification sounds": the message sound's switch (Settings > Sounds). */
export function notificationSoundsOn(prefs = useSounds.getState().prefs): boolean {
  return !prefs.off.message;
}

export function setNotificationSounds(on: boolean): void {
  useSounds.getState().setKind('message', on);
}
